import { useEffect, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { WifiOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * NetworkErrorRecovery - Global network error detection and recovery UI
 * 
 * Monitors network connectivity and provides users with:
 * - Visual notification when offline
 * - Automatic reconnection detection
 * - Manual retry capability
 * - Toast notifications for status changes
 */
export const NetworkErrorRecovery = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (wasOffline) {
        toast.success("Connection restored", {
          description: "You're back online. All pending actions will resume.",
        });
        setWasOffline(false);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      toast.error("Connection lost", {
        description: "You're offline. Some features may be unavailable.",
        duration: Infinity, // Stay until dismissed or online
      });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [wasOffline]);

  const handleRetry = () => {
    toast.info("Checking connection...");
    // Force a connection check
    fetch("/favicon.ico", { method: "HEAD", cache: "no-cache" })
      .then(() => {
        setIsOnline(true);
        toast.success("Connection successful");
      })
      .catch(() => {
        toast.error("Still offline", {
          description: "Please check your internet connection and try again.",
        });
      });
  };

  if (isOnline) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md animate-slide-in-bottom">
      <Alert variant="destructive" className="border-2">
        <WifiOff className="h-5 w-5" />
        <AlertTitle className="font-semibold">No Internet Connection</AlertTitle>
        <AlertDescription className="space-y-3">
          <p className="text-sm">
            You're currently offline. Some features may not work properly.
          </p>
          <Button
            onClick={handleRetry}
            variant="outline"
            size="sm"
            className="w-full"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry Connection
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
};
